import { DropoffPricingMode } from "@redotech/congo-enums/dropoff-pricing-mode";
import { Currency } from "@redotech/money/currencies";
import { dropoffProviderType } from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const boxlessDropoffPricing = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal(DropoffPricingMode.Default) }),
  z.object({
    mode: z.literal(DropoffPricingMode.Fixed),
    amount: z.number().positive(),
    /** Everything priced so far is USD, so an unset currency is USD. */
    currency: z.enum(Currency).default(Currency.USD),
  }),
  z.object({
    mode: z.literal(DropoffPricingMode.Markup),
    /** Flat amount added, in the standard price's currency. Absent for a percentage. */
    amount: z.number().min(0).optional(),
    /** Percent of the standard price added, e.g. 10 for 10%. Absent for a flat amount. */
    percent: z.number().min(0).optional(),
  }),
]);

export type BoxlessDropoffPricing = z.infer<typeof boxlessDropoffPricing>;

const isMarkup = (
  pricing: BoxlessDropoffPricing,
): pricing is Extract<
  BoxlessDropoffPricing,
  { mode: typeof DropoffPricingMode.Markup }
> => pricing.mode === DropoffPricingMode.Markup;

/**
 * A markup is a flat amount or a percentage, never both. Only a write is held
 * to this, so a read keeps reporting whatever is stored.
 */
const boxlessDropoffPricingWrite = boxlessDropoffPricing
  .refine(
    (pricing) =>
      !isMarkup(pricing) ||
      (pricing.amount ?? 0) <= 0 ||
      (pricing.percent ?? 0) <= 0,
    { message: "A markup is either a flat amount or a percentage, not both" },
  )
  .refine(
    (pricing) =>
      !isMarkup(pricing) ||
      (pricing.amount ?? 0) > 0 ||
      (pricing.percent ?? 0) > 0,
    { message: "A markup needs a flat amount or a percentage above zero" },
  );

const boxlessDropoffFee = z.object({
  provider: z.enum(dropoffProviderType),
  pricing: boxlessDropoffPricing,
  /** Whether this provider is configurable for the team (e.g. FedEx Easy Returns needs an integration). */
  available: z.boolean(),
});

export const getBoxlessDropoffFees = unaryRpc({
  name: "getBoxlessDropoffFees" as const,
  input: z.object({ teamId: z.string() }),
  output: z.object({ fees: z.array(boxlessDropoffFee) }),
});

export const updateBoxlessDropoffFee = unaryRpc({
  name: "updateBoxlessDropoffFee" as const,
  input: z.object({
    teamId: z.string(),
    provider: z.enum(dropoffProviderType),
    pricing: boxlessDropoffPricingWrite,
  }),
  output: boxlessDropoffFee,
});
